import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, ArrowRight, Video, BookOpen, CreditCard, Check, Lock } from 'lucide-react';
import { PageLayout } from '../components/layout';
import { OnboardingStep } from '../components/onboarding/OnboardingStep';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import { useOnboarding } from '../hooks/useOnboarding';
import { OnboardingStepKey } from '../types/onboarding';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { WebsiteFetchModal } from '../components/website/WebsiteFetchModal';

// Define the Skeleton Loader component for onboarding steps
const OnboardingSkeletonLoader: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      {Array(4).fill(0).map((_, index) => (
        <React.Fragment key={index}>
          {/* Connecting line skeleton */}
          {index > 0 && (
            <div className="flex justify-center">
              <div className="h-4 w-0.5 bg-gray-200 animate-pulse"></div>
            </div>
          )}
          {/* Card skeleton */}
          <div className="rounded-xl border border-gray-200 p-5 shadow-md bg-white animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              {/* Step indicator circle skeleton */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-200"></div>
              {/* Title skeleton */}
              <div className="h-5 bg-gray-200 rounded w-1/3"></div>
            </div>
            {/* Description skeleton */}
            <div className="space-y-2 pl-[3.25rem] mb-4">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            </div>
            {/* Action button skeleton */}
            <div className="flex justify-end">
              <div className="h-8 w-24 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// Fonction pour obtenir le message d'encouragement
const getEncouragementMessage = (percentage: number, t: Function) => {
  if (percentage === 0) {
    return t('onboarding.encouragement.start', 'Let\'s get started!');
  }
  if (percentage < 30) {
    return t('onboarding.encouragement.goodStart', 'Good start, keep going!');
  }
  if (percentage < 70) {
    return t('onboarding.encouragement.halfway', 'Great job! You\'re over halfway there!');
  }
  if (percentage < 100) {
    return t('onboarding.encouragement.almostThere', 'Almost there, just a few more steps!');
  }
  return t('onboarding.encouragement.done', 'Congratulations! You completed the onboarding!');
};

const Onboarding = () => {
  const { t } = useTranslation();
  const { onboardingProgress, isLoading, completeStep, growth } = useOnboarding();
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const prevPercentageRef = useRef(0);
  const { user, isLoading: isAuthLoading } = useAuthStore();
  const showBanner = true;
  
  // State for website fetch modal - show only on first visit
  const [isWebsiteFetchModalOpen, setIsWebsiteFetchModalOpen] = useState(false);
  const [hasShownWebsiteModal, setHasShownWebsiteModal] = useState(false);

  // Show loading state if auth or onboarding data is loading
  if (isAuthLoading || (!user && !isLoading)) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }
  
  useEffect(() => {
    document.title = t('onboarding.title', 'Onboarding');
  }, [t]);

  // Check if this is the first time visiting onboarding page and show website modal
  useEffect(() => {
    if (!user || isLoading || hasShownWebsiteModal) return;
    
    // Create a unique key for each user to track their first visit
    const storageKey = `socialness_first_onboarding_visit_${user.id}`;
    const hasVisitedBefore = localStorage.getItem(storageKey);
    
    if (!hasVisitedBefore) {
      // Only show modal if user hasn't completed brand analysis yet
      const brandAnalysisCompleted = onboardingProgress?.steps?.brand_analysis;
      
      if (!brandAnalysisCompleted) {
        // Small delay to ensure page is loaded
        const timer = setTimeout(() => {
          setIsWebsiteFetchModalOpen(true);
          setHasShownWebsiteModal(true);
          // Mark that user has seen the modal
          localStorage.setItem(storageKey, 'true');
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [user, isLoading, onboardingProgress, hasShownWebsiteModal]);

  // Get first name from the user's name and capitalize it
  const namePart = user?.name ? user.name.split(' ')[0] : '';
  const firstName = namePart.charAt(0).toUpperCase() + namePart.slice(1);

  // Définition des keyframes pour l'animation des confettis
  useEffect(() => {
    // Création d'une feuille de style pour les animations
    const styleSheet = document.createElement("style");
    styleSheet.id = "confetti-animations";
    
    // Définition de l'animation simple de fade in pour les confettis
    styleSheet.textContent = `
      @keyframes fade-in-confetti {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
    `;
    
    // Ajout de la feuille de style au document si elle n'existe pas déjà
    if (!document.getElementById("confetti-animations")) {
      document.head.appendChild(styleSheet);
    }
    
    // Nettoyage à la démonter
    return () => {
      const existingStyle = document.getElementById("confetti-animations");
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Animation du pourcentage lorsqu'il change
  useEffect(() => {
    const currentPercentage = onboardingProgress?.percentageCompleted || 0;
    const prevPercentage = prevPercentageRef.current;
    
    prevPercentageRef.current = currentPercentage;
    
    if (currentPercentage === prevPercentage) {
      setAnimatedPercentage(currentPercentage);
      return;
    }
    
    let startTime: number | null = null;
    const duration = 1500; // durée de l'animation en ms
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const newValue = prevPercentage + (currentPercentage - prevPercentage) * progress;
      setAnimatedPercentage(Math.round(newValue));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [onboardingProgress?.percentageCompleted]);

  // Reusable Progress Card (used in mobile at top and in right column on desktop)
  const ProgressCard: React.FC = () => (
    <div className="bg-white rounded-xl border border-black p-6 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
      {/* Confettis décoratifs en arrière-plan */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(25)].map((_, i) => {
          const shapes = [
            'w-4 h-1 rounded-full',
            'w-1 h-4 rounded-full',
            'w-2 h-2 rounded-full',
            'w-3 h-3 rounded-full',
            'w-2 h-2 rotate-45',
          ];
          const colors = [
            'bg-primary-400',
            'bg-yellow-400',
            'bg-red-400',
            'bg-purple-400'
          ];
          const shape = shapes[i % shapes.length];
          const color = colors[i % colors.length];
          return (
            <div
              key={i}
              className={`absolute ${shape} ${color}`}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: 0,
                animation: `fade-in-confetti ${Math.random() * 0.5 + 0.5}s forwards ${Math.random() * 1.5}s`,
                transform: `rotate(${Math.random() * 180}deg)`
              }}
            />
          );
        })}
      </div>

      <div className="flex flex-col items-center text-center relative z-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          {currentPercentage === 100
            ? t('onboarding.progress.completed', 'Well done!')
            : t('onboarding.progress.title', 'Your Progress 🤩')}
        </h3>

        <div className="relative inline-flex mb-6 w-36 h-36">
          <div className="w-full h-full rounded-full bg-transparent absolute"></div>
          <svg className="w-full h-full absolute top-0 left-0" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={currentPercentage === 100 ? '#E9B949' : '#22C55E'}
              strokeWidth="8"
              strokeDasharray={`${(animatedPercentage * 283) / 100} 283`}
              strokeDashoffset="0"
              transform="rotate(-90 50 50)"
              strokeLinecap="round"
              className="transition-all duration-300 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {currentPercentage === 100 ? (
              <div className="bg-yellow-400 rounded-full p-2 w-16 h-16 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            ) : (
              <div className="text-center">
                <span className="text-4xl font-bold text-gray-900">{animatedPercentage}%</span>
              </div>
            )}
          </div>
        </div>

        <p className={`${currentPercentage === 100 ? 'text-xl' : 'text-base'} text-gray-700 font-medium`}>
          {currentPercentage === 100
            ? t('onboarding.progress.allSteps', 'You completed all steps.')
            : currentPercentage < 30
              ? t('onboarding.progress.lowCompletion', "You're just getting started!")
              : currentPercentage < 70
                ? t('onboarding.encouragement.halfway', "Great job! You're over halfway there!")
                : t('onboarding.progress.highCompletion', 'Almost there!')}
        </p>
      </div>
    </div>
  );

  // Configuration des étapes d'onboarding avec les clés d'action
  // Build title suffix from growth map provided by the hook
  const getGrowthSuffix = (key: OnboardingStepKey): string => {
    const value = growth?.[key];
    return typeof value === 'number' ? ` (+${value}%)` : '';
  };

  const steps = [
    // 1. Brand (+20%)
    {
      id: 1,
      key: 'brand_analysis' as OnboardingStepKey,
      title: `${t('onboarding.steps.brand_analysis.title', 'Analyser sa marque')}${getGrowthSuffix('brand_analysis')}`,
      description: t('onboarding.steps.brand_analysis.description', 'Utilisez l\'IA pour analyser votre site web et obtenir une stratégie de marque complète et personnalisée'),
      link: '/brand-strategy-lab?modal=analyze',
      actionTextKey: 'onboarding.actions.startAnalyze'
    },
    // 2. Persona (+15%)
    {
      id: 2,
      key: 'persona_creation' as OnboardingStepKey,
      title: `${t('onboarding.steps.persona_creation.title', 'Créer un persona')}${getGrowthSuffix('persona_creation')}`,
      description: t('onboarding.steps.persona_creation.description', 'Générez des profils détaillés de votre audience idéale avec l\'IA pour mieux cibler votre contenu'),
      link: '/ai-personas-builder?modal=create',
      actionTextKey: 'onboarding.actions.startCreatePersona'
    },
    // 3. Account (+20%)
    {
      id: 3,
      key: 'social_account_connection' as OnboardingStepKey,
      title: `${t('onboarding.steps.social_account_connection.title', 'Connecter un compte réseau social')}${getGrowthSuffix('social_account_connection')}`,
      description: t('onboarding.steps.social_account_connection.description', 'Connectez vos comptes de réseaux sociaux pour programmer et publier automatiquement'),
      link: '/social-media-hub?modal=connect',
      actionTextKey: 'onboarding.actions.startConnectAccount'
    },
    // 4. Analytics (+20%)
    {
      id: 4,
      key: 'ai_analytics' as OnboardingStepKey,
      title: `${t('onboarding.steps.ai_analytics.title', 'Voir ses analytics analysés par l\'IA')}${getGrowthSuffix('ai_analytics')}`,
      description: t('onboarding.steps.ai_analytics.description', 'Obtenez des insights intelligents sur la performance de vos publications grâce à l\'analyse IA'),
      link: '/analytics',
      actionTextKey: 'onboarding.actions.startViewAnalytics'
    },
    // 5. Links (+10%)
    {
      id: 5,
      key: 'shorten_link' as OnboardingStepKey,
      title: `${t('onboarding.steps.shorten_link.title', 'Create a smart trackable link')}${getGrowthSuffix('shorten_link')}`,
      description: t(
        'onboarding.steps.shorten_link.description',
        'Use the Smart Link Builder and Shortener to generated automatically a trackable link and shorten it to make it easier to share.'
      ),
      link: '/ai-links-builder?modal=create',
      actionTextKey: 'onboarding.actions.trackYourLink'
    },
    // 6. Content (+10%)
    {
      id: 6,
      key: 'content_creation' as OnboardingStepKey,
      title: `${t('onboarding.steps.content_creation.title', 'Créer du contenu')}${getGrowthSuffix('content_creation')}`,
      description: t('onboarding.steps.content_creation.description', 'Créez du contenu pour vos réseaux sociaux adapté à votre marque et personas grâce à l\'IA'),
      link: '/content-creation',
      actionTextKey: 'onboarding.actions.startCreateContent'
    },
    // 7. Review your post (+5%)
    {
      id: 7,
      key: 'post_review' as OnboardingStepKey,
      title: `${t('onboarding.steps.post_review.title', 'Ask AI to review your post')}${getGrowthSuffix('post_review')}`,
      description: t(
        'onboarding.steps.post_review.description',
        'Not sure about your next post? Ask AI directly in the app to check what can be improved and why?'
      ),
      link: '/posts/new',
      actionTextKey: 'onboarding.actions.startReviewPost'
    }
  ];

  // Déterminez quelle étape est active (la première non complétée)
  const firstIncompleteStep = steps.find(
    step => !onboardingProgress?.steps[step.key]
  );
  
  const activeStepId = firstIncompleteStep?.id || steps[steps.length - 1].id + 1; // Show all completed if done

  // Fonction pour marquer une étape comme complète (logique simplifiée pour l'instant)
  const handleStartStep = (step: OnboardingStepKey) => {
    // La logique de complétion se fait maintenant via l'API quand l'action est réellement effectuée
    console.log(`Starting step: ${step}`);
  };

  // Vérifie si une étape est bloquée selon des règles métier spécifiques
  const isStepBlocked = (stepKey: OnboardingStepKey) => {
    const completed = onboardingProgress?.steps || {} as Record<string, boolean>;
    switch (stepKey) {
      case 'persona_creation':
        // On ne peut pas faire Persona sans Brand
        return !completed.brand_analysis;
      case 'shorten_link':
        // On ne peut pas faire Link sans Brand
        return !completed.brand_analysis;
      case 'content_creation':
        // On ne peut pas faire Content sans Brand ET Persona
        return !(completed.brand_analysis && completed.persona_creation);
      case 'ai_analytics':
        // On ne peut pas faire Analytics sans Accounts
        return !completed.social_account_connection;
      default:
        // Toutes les autres étapes ne sont pas bloquées
        return false;
    }
  };

  const currentPercentage = onboardingProgress?.percentageCompleted || 0;
  const encouragementMessage = getEncouragementMessage(currentPercentage, t);

  const handleScheduleTimeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const chatwoot = window.$chatwoot;
    // Keep the email link usable if the widget is blocked or still loading.
    if (!chatwoot?.hasLoaded) return;

    try {
      chatwoot.toggle('open');
      event.preventDefault();
    } catch (error) {
      console.warn('Chatwoot: could not open chat', error);
      return;
    }

    const demoRequestMessage = t('onboarding.videoCall.demoRequestMessage', "Hey, I want to book a demo!");
    // Flag the request on the contact/conversation; no message is sent for them.
    try {
      chatwoot.setCustomAttributes({ demo_request: demoRequestMessage });
      chatwoot.setLabel('demo-request');
    } catch (error) {
      console.warn('Chatwoot: could not tag the demo request', error);
    }
  };

  // Handle closing website fetch modal
  const handleCloseWebsiteFetchModal = () => {
    setIsWebsiteFetchModalOpen(false);
  };

  return (
    <PageLayout
      title={t('onboarding.title', 'Onboarding')}
      subtitle={t('onboarding.subtitle', 'Some explanation here on overview')}
    >

        {/* Top banner: choose your onboarding path (temporarily hidden) */}
        {showBanner && (
          <div className="mb-8">
            <OnboardingBanner />
          </div>
        )}

        {/* Mobile-first progress card */}
        <div className="mb-6 md:hidden">
          <ProgressCard />
        </div>

        {/* Grid layout with onboarding cards on left and promotional cards on right */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Onboarding cards - takes 2/3 of space */}
          <div className="md:col-span-2">
            {/* Section heading: The journey */}
            <h2 className="font-title text-xl font-semibold text-gray-900 mb-2">
              {t('onboarding.banner.journeyTitle', 'The journey')}
            </h2>
            {/* Grid of individual step cards */}
            {isLoading ? (
              <OnboardingSkeletonLoader />
            ) : (
              <div className="space-y-4">
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    {/* Connecting line between cards */}
                    {index > 0 && (
                      <div className="flex justify-center">
                        <div className={`h-4 w-0.5 ${
                          onboardingProgress?.steps[steps[index-1].key] 
                            ? 'bg-green-500' 
                            : 'bg-gray-200'
                        }`}></div>
                      </div>
                    )}
                      <div 
                      className={`rounded-xl border p-5 shadow-md hover:shadow-lg transition-shadow ${
                        isStepBlocked(step.key) 
                          ? 'border-gray-200 bg-white' 
                          : step.id === activeStepId 
                            ? 'border-primary-500 ring-2 ring-primary-200 bg-white' 
                            : onboardingProgress?.steps[step.key] 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-black bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        {/* Step indicator circle */}
                        <div 
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ease-in-out ${
                            onboardingProgress?.steps[step.key] 
                              ? 'bg-green-500 text-white shadow-md'
                              : step.id === activeStepId 
                                ? 'bg-primary-500 text-white ring-2 ring-primary-200 animate-pulse shadow-lg'
                                : isStepBlocked(step.key)
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {onboardingProgress?.steps[step.key] ? (
                            <Check className="w-6 h-6" />
                          ) : isStepBlocked(step.id) ? (
                            <Lock className="w-5 h-5" />
                          ) : (
                            <div className="font-semibold text-lg">{step.id}</div>
                          )}
                        </div>
                        
                        <h3 
                          className={`text-lg font-semibold ${
                            onboardingProgress?.steps[step.key] 
                              ? 'text-gray-500' 
                              : step.id === activeStepId 
                                ? 'text-gray-900' 
                                : 'text-gray-500'
                          }`}
                        >
                          {step.title}
                        </h3>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4 pl-[3.25rem]">
                        {step.description}
                      </p>
                      
                      {/* Action button */}
                      <div className="flex justify-end">
                        {onboardingProgress?.steps[step.key] && !isStepBlocked(step.key) && (
                          <Link 
                            to={step.link} 
                            className="inline-flex items-center px-4 py-1 text-sm bg-white border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition shadow-sm"
                          >
                            {t('onboarding.actions.redo', 'Do again')}
                          </Link>
                        )}
                        
                        {step.id === activeStepId && !isStepBlocked(step.key) && (
                          <Link
                            to={step.link}
                            onClick={() => handleStartStep(step.key)}
                            className="inline-flex items-center px-4 py-1 text-sm bg-primary-600 text-white border border-primary-600 rounded-lg hover:bg-primary-700 transition shadow-sm"
                          >
                            {t(step.actionTextKey || 'onboarding.actions.start', 'Commencer')}
                          </Link>
                        )}
                        
                        {isStepBlocked(step.key) && (
                          <div 
                            className="inline-flex items-center px-4 py-1 text-sm bg-gray-200 text-gray-500 border border-gray-300 rounded-lg cursor-not-allowed shadow-sm"
                            title={t('onboarding.tooltips.locked', 'Complétez l\'étape précédente pour déverrouiller')}
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            {t('onboarding.actions.locked', 'Verrouillé')}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Connecting line after the last card (to show more steps might come) */}
                    {index === steps.length - 1 && !onboardingProgress?.steps[step.key] && (
                      <div className="flex justify-center">
                        <div className="h-4 w-0.5 bg-gray-200"></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Right column for promotional cards - takes 1/3 of space */}
          <div className="md:col-span-1 space-y-6 md:mt-8">
            {/* Carte de taux de complétion (desktop only) */}
            <div className="hidden md:block">
              <ProgressCard />
            </div>

            {/* Première carte - Appel vidéo personnalisé */}
            <div className="bg-white rounded-xl border border-black p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <Video className="w-8 h-8 text-primary-500 mb-2" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('onboarding.videoCall.title', 'Réservez votre appel vidéo personnalisé')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t('onboarding.videoCall.description', 'Obtenez une visite guidée de 30 minutes pour garantir une configuration optimale')}
                  </p>
                </div>
                <div className="mt-auto flex flex-wrap gap-4">
                  <a
                    href={`mailto:hello@socialness.ai?subject=${encodeURIComponent(t('onboarding.videoCall.demoRequestMessage', "Hey, I want to book a demo!"))}`}
                    onClick={handleScheduleTimeClick}
                    className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                  >
                    {t('onboarding.videoCall.cta', 'Schedule time')}
                  </a>
                </div>
              </div>
            </div>

            {/* Deuxième carte - Choisir un plan */}
            <div className="bg-white rounded-xl border border-black p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <CreditCard className="w-8 h-8 text-primary-500 mb-2" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('onboarding.choosePlan.title', 'Choisissez votre forfait')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t('onboarding.choosePlan.description', 'Sélectionnez le forfait adapté à votre entreprise et débloquez toutes les fonctionnalités premium')}
                  </p>
                </div>
                <div className="mt-auto flex flex-wrap gap-4">
                  <Link
                    to="/billing"
                    className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                  >
                    {t('onboarding.choosePlan.cta', 'View plans')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Website Fetch Modal - shown only on first visit */}
        <WebsiteFetchModal
          isOpen={isWebsiteFetchModalOpen}
          onClose={handleCloseWebsiteFetchModal}
        />
    </PageLayout>
  );
};

export default Onboarding;
